import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import RestaurantOrderService, {
  RestaurantOrderBillingDTO,
} from '../../services/restaurant/RestaurantOrderService';
import { RootState } from '../../store/store';
import { RestaurantOrderDTO } from '../../types/restaurant/RestaurantOrderTypes';
import { saveRestaurantBillingContext } from '../../features/restaurant/billing/restaurantBillingContext';
import useApiErrorHandler from '../useApiErrorHandler';
import useAlerts from '../useAlerts';
import useContinueSale from '../sale/useContinueSale';

interface Options {
  order: RestaurantOrderDTO | null;
  setOrder: (order: RestaurantOrderDTO | null) => void;
  refreshTables: () => Promise<void>;
  onOrderClosed?: () => Promise<void> | void;
}

const useRestaurantOrderBilling = ({
  order,
  setOrder,
  refreshTables,
  onOrderClosed,
}: Options) => {
  const { handle } = useApiErrorHandler();
  const { show } = useAlerts();
  const continueSale = useContinueSale();
  const realtimeSale = useSelector(
    (state: RootState) => state.saleDteRealtime.current
  );

  const [billing, setBilling] = useState<RestaurantOrderBillingDTO | null>(
    null
  );
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [startingBilling, setStartingBilling] = useState(false);
  const [billingMessage, setBillingMessage] = useState<string | null>(null);
  const startingRef = useRef(false);

  const refreshOrder = useCallback(
    async (orderId: number) => {
      const response = await RestaurantOrderService.get(orderId);
      setOrder(response.data);
      await refreshTables();
      if (response.data.status === 'CLOSED') {
        await onOrderClosed?.();
      }
      return response.data;
    },
    [onOrderClosed, refreshTables, setOrder]
  );

  const refreshBilling = useCallback(
    async (orderId: number) => {
      setLoadingBilling(true);
      try {
        const response = await RestaurantOrderService.getBilling(orderId);
        setBilling(response.data);
        setBillingMessage(
          response.data.reason ||
            response.data.message ||
            response.message ||
            null
        );

        if (response.data.status === 'ACCEPTED') {
          await refreshOrder(orderId);
        }

        return response.data;
      } catch (error) {
        handle(error);
        return null;
      } finally {
        setLoadingBilling(false);
      }
    },
    [refreshOrder]
  );

  useEffect(() => {
    setBilling(null);
    setBillingMessage(null);
    if (order?.status === 'BILLING') {
      refreshBilling(order.id);
    }
  }, [order?.id, order?.status, refreshBilling]);

  useEffect(() => {
    if (!order || !billing || realtimeSale?.saleId !== billing.saleId) return;
    refreshBilling(order.id);
  }, [
    billing?.saleId,
    order?.id,
    realtimeSale?.saleId,
    realtimeSale?.status,
    refreshBilling,
  ]);

  const startBilling = useCallback(
    async (documentTypeId: number) => {
      const retryable =
        order?.status === 'BILLING' &&
        (billing?.status === 'REJECTED' || billing?.status === 'CANCELLED');

      if (
        !order ||
        (order.status !== 'READY' && !retryable) ||
        startingRef.current
      ) {
        return false;
      }

      if (!order.customerId) {
        show(
          'warning',
          'Cliente requerido',
          'Asigne un cliente a la orden antes de iniciar el cobro.'
        );
        return false;
      }

      startingRef.current = true;
      setStartingBilling(true);

      try {
        const response = await RestaurantOrderService.billing(order.id, {
          documentTypeId,
        });

        saveRestaurantBillingContext({
          orderId: order.id,
          saleId: response.data.id,
          branchId: order.branchId,
          tableId: order.tableId,
        });

        show(
          response.type ?? 'success',
          response.title ?? 'Cobro iniciado',
          response.message ?? 'La orden fue enviada a caja.'
        );
        continueSale(response.data);
        return true;
      } catch (error) {
        handle(error);
        return false;
      } finally {
        startingRef.current = false;
        setStartingBilling(false);
      }
    },
    [billing?.status, continueSale, handle, order, show]
  );

  const continueBilling = useCallback(() => {
    if (billing?.status !== 'PENDING' || !billing.continuation) return false;
    continueSale(billing.continuation);
    return true;
  }, [billing, continueSale]);

  return {
    billing,
    billingMessage,
    loadingBilling,
    startingBilling,
    refreshBilling,
    startBilling,
    continueBilling,
  };
};

export default useRestaurantOrderBilling;
